<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n, type MessageKey } from '@/app/i18n'
import { sharedViewMeta } from '@/app/router/shared-view'
import { useCurrentSoftware } from '@/shared/composables/useCurrentSoftware'
import AppIcon from '@/shared/ui/AppIcon.vue'

/**
 * The breadcrumb row (wp/shell §4.19): shown when the route has `meta.breadcrumb` or `meta.pageTitle`; a home icon to
 * `/`, then one item per breadcrumb entry (texts are i18n keys, translated at render — so routes that set them in
 * `beforeEnter` work too). The page title is not displayed (legacy parity, OPEN-9 default (a)). On a route shared with
 * another software (`meta.sharedWith`), that software's users get its `meta.sharedView` crumbs (queue #28).
 */
const route = useRoute()
const { t } = useI18n()
const { software } = useCurrentSoftware()
// Breadcrumb texts are message keys declared with keys.t() in the route files.
// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
const label = (key: string): string => t(key as MessageKey)
const breadcrumb = computed(() => sharedViewMeta(route.meta, software.value).breadcrumb)
const items = computed(() => breadcrumb.value ?? [])
const visible = computed(() => Boolean(breadcrumb.value || route.meta.pageTitle))
</script>

<template>
  <nav v-if="visible" class="mb-6" aria-label="breadcrumb" data-testid="breadcrumb">
    <ol class="flex flex-wrap items-center gap-2 text-[0.95rem]">
      <li>
        <RouterLink to="/" class="text-primary" :aria-label="t('common.nav.home')"
          ><AppIcon name="HomeIcon" :size="16"
        /></RouterLink>
      </li>
      <li v-for="(item, index) in items" :key="index" class="flex items-center gap-2">
        <AppIcon name="ChevronRightIcon" :size="14" class="text-muted" />
        <RouterLink v-if="item.to && !item.active" :to="item.to" class="text-primary">{{
          label(item.text)
        }}</RouterLink>
        <span v-else :aria-current="item.active ? 'page' : undefined">{{ label(item.text) }}</span>
      </li>
    </ol>
  </nav>
</template>
